import { useMemo, useRef } from 'react';
import { PiArrowDown } from 'react-icons/pi';
import type { TCEmployeeExecution } from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { CardSkeleton } from 'src/modules/shared/components/atoms/cards/card-skeleton';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { DateGroupItems } from 'src/modules/shared/components/molecules/date-group/date-group-items';
import { useInfiniteScroll } from 'src/modules/shared/hooks/scroll/movement';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { toCalendarDateToken } from 'src/modules/shared/utils/date-range.util';
import EmployeeExecutionCard from 'src/modules/automations/components/molecules/employees/executions/execution-card';

interface ExecutionsPanelProps {
  /** Loaded execution records. */
  executions: TCEmployeeExecution[];
  /** Opens one execution. */
  onExecutionSelect: (executionId: string) => void;
  /** Loads the next page. */
  onLoadMore: () => void;
  /** Whether another page is available. */
  hasMore: boolean;
  /** Whether the next page is loading. */
  isLoadingMore: boolean;
  /** Whether the first page is loading. */
  isInitialLoading: boolean;
}

interface ExecutionDateGroup {
  /** Local calendar date shared by the group. */
  date: Date;
  /** Executions in API order. */
  executions: TCEmployeeExecution[];
}

const groupExecutionsByDate = (
  executions: TCEmployeeExecution[]
): ExecutionDateGroup[] => {
  const groups = new Map<string, ExecutionDateGroup>();

  executions.forEach((execution) => {
    const date = new Date(execution.createdAt);
    const key = toCalendarDateToken(date);
    const group = groups.get(key);

    if (group) {
      group.executions.push(execution);
    } else {
      groups.set(key, { date, executions: [execution] });
    }
  });

  return Array.from(groups.values());
};

const ExecutionsLoadingState: React.FC = () => (
  <div className="space-y-3">
    {Array.from({ length: 6 }, (_, index) => (
      <CardSkeleton
        key={index}
        className="min-h-28 border border-neutral-100"
        header={{ title: true, subtitle: true, action: true }}
        content={{ lines: 2, widths: ['w-2/3', 'w-full'] }}
      />
    ))}
  </div>
);

/** Grouped, responsive AI Agent execution list. */
const EmployeeExecutionsPanel: React.FC<ExecutionsPanelProps> = ({
  executions,
  onExecutionSelect,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isInitialLoading,
}) => {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const groups = useMemo(() => groupExecutionsByDate(executions), [executions]);

  useInfiniteScroll({
    containerRef,
    isFetching: isLoadingMore,
    hasMore,
    fetchMore: onLoadMore,
    direction: 'down',
    threshold: 0.3,
  });

  return (
    <div
      ref={containerRef}
      className="min-h-0 flex-1 overflow-auto bg-slate-50 p-4 sm:p-5"
      aria-label={t('automations.aiAgents.executions.title')}
      data-testid="executions-scroll"
    >
      <div className="mx-auto w-full max-w-7xl">
        {isInitialLoading ? (
          <ExecutionsLoadingState />
        ) : executions.length === 0 ? (
          <div className="flex min-h-96 items-center justify-center">
            <EmptyState
              title={t('automations.aiAgents.executions.emptyTitle')}
              description={t(
                'automations.aiAgents.executions.emptyDescription'
              )}
            />
          </div>
        ) : (
          <div className="space-y-5">
            {groups.map((group) => (
              <DateGroupItems
                key={toCalendarDateToken(group.date)}
                date={group.date}
              >
                {group.executions.map((execution) => (
                  <EmployeeExecutionCard
                    key={execution.executionId}
                    execution={execution}
                    onSelect={() => onExecutionSelect(execution.executionId)}
                  />
                ))}
              </DateGroupItems>
            ))}

            {hasMore || isLoadingMore ? (
              <Button
                type="button"
                variant="secondary"
                appearance="outline"
                loading={isLoadingMore}
                disabled={isLoadingMore}
                onClick={onLoadMore}
                className="w-full gap-2"
              >
                <PiArrowDown className="h-4 w-4" />
                {t('automations.aiAgents.actions.loadMore')}
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeExecutionsPanel;
